import useAPI, {
  useAPIMutation,
  useDistinctFilterOptions
} from "@/services/api/hook";
import { useQueryClient } from "@tanstack/react-query";
import {
  APIObject,
  ComputedFields,
  Filters,
  SearchQuery
} from "@/services/api/types";
import { PartnerData } from "./types";
import useBoundStore, {
  MAX_ENTITIES_PER_PAGE
} from "@/services/store/BoundStore";
import { refreshEntityDetail, useEntities } from "@/services/api/entityhooks";
import { EntitySliceState } from "@/services/api/entitystore";
import { createUseFilteredEntitiesInfinite } from "@/services/api/infinite";
import { useAccountSettings } from "@/entities/settings/hooks";
import { extractKeyValuePairs } from "@/pages/settings/pages/GeneralSettings";

export const MAX_PARTNERS_PER_PAGE = MAX_ENTITIES_PER_PAGE;

const ENABLE_PARTNERSHIPS_KEY = "enable_partnerships";

export const useEnablePartnerships = (): boolean => {
  const { data: accountSettings } = useAccountSettings();
  const keyValuePairs = accountSettings
    ? extractKeyValuePairs(accountSettings)
    : {};
  return Boolean(keyValuePairs?.[ENABLE_PARTNERSHIPS_KEY]);
};

export const usePartners = (
  ids: string[],
  options: {
    enabled?: boolean;
    computed_fields?: ComputedFields;
    forceRefetch?: boolean;
    include_additionals?: boolean;
  } = {}
) => {
  const forceRefetch = options.forceRefetch ?? false;
  const enabled = options.enabled;
  const include_additionals = options.include_additionals;

  const usePartnersStore = (): EntitySliceState<PartnerData> => {
    return useBoundStore((state) => state.partners);
  };

  return useEntities<PartnerData>(
    ids,
    "partners",
    usePartnersStore,
    (endpoint, params, apiOptions) =>
      useAPI(
        endpoint,
        {
          ...params,
          queryParams: {
            ...(options.computed_fields && {
              computed_fields: options.computed_fields
            }),
            ...(include_additionals && {
              include_additionals: "true"
            })
          }
        },
        { ...apiOptions, enabled }
      ),
    undefined,
    options.computed_fields,
    forceRefetch
  );
};

export const useUpdatePartnerMutation = () => {
  const updatePartner = useBoundStore((state) => state.partners.updateEntity);
  const queryClient = useQueryClient();
  return useAPIMutation<
    { pathParams: { partnerId: string }; data: PartnerData },
    PartnerData
  >(
    "partners/update",
    {
      method: "PUT"
    },
    (response) => {
      updatePartner(response.id, response as Partial<PartnerData>);
      refreshEntityDetail(queryClient, "partners", response.id);
      queryClient.invalidateQueries({ queryKey: ["partners/filter"] });
      queryClient.invalidateQueries({ queryKey: ["partners/distinctValues"] });
    }
  );
};

export const useCreatePartnerMutation = () => {
  const addPartner = useBoundStore((state) => state.partners.addEntity);
  return useAPIMutation<PartnerData, APIObject>(
    "createPartner",
    {
      method: "POST"
    },
    (response, data) => {
      addPartner({
        ...data,
        id: response.id
      } as PartnerData);
    }
  );
};

export const {
  useFilteredEntitiesInfinite,
  useResetFilteredEntitiesInfinite: useResetPartnersInfinite
} = createUseFilteredEntitiesInfinite<PartnerData>(
  "partners",
  MAX_PARTNERS_PER_PAGE
);

export const usePartnersInfinite = (
  filters?: Filters,
  search?: SearchQuery,
  groupedBy?: keyof PartnerData,
  pageSize = MAX_PARTNERS_PER_PAGE,
  sortField?: string,
  sortAsc?: boolean,
  fetchAll?: boolean,
  enabled = true,
  computed_fields: ComputedFields = ComputedFields.ALL
) => {
  return useFilteredEntitiesInfinite(
    () => ({ addEntity: useBoundStore((state) => state.partners.addEntity) }),
    {
      filters,
      search,
      groupedBy,
      pageSize,
      sortField,
      sortAsc,
      fetchAll,
      enabled,
      computed_fields
    }
  );
};

export const useDeletePartnerMutation = () => {
  const removePartner = useBoundStore((state) => state.partners.removeEntity);
  const queryClient = useQueryClient();
  return useAPIMutation<{ pathParams: { partnerId: string } }, void>(
    "partners/id",
    {
      method: "DELETE"
    },
    (_response, data) => {
      removePartner(data.pathParams.partnerId);
      queryClient.invalidateQueries({ queryKey: ["partners/filter"] });
      queryClient.invalidateQueries({ queryKey: ["partners/distinctValues"] });
    }
  );
};

export const usePartnersDistinctFilterOptions = (
  fieldName: string,
  filters?: Filters,
  searchTerm?: string
) => {
  return useDistinctFilterOptions("partners", fieldName, filters, searchTerm);
};

export default {
  usePartners,
  useUpdatePartnerMutation,
  useCreatePartnerMutation,
  usePartnersInfinite,
  useResetPartnersInfinite
};
